import { css } from 'aphrodite';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FontSelection } from '../../../extViewmodels';
import { parseGoogleFontInput } from '../../../utils/googleFontUtils';
import { Button } from '../Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../Dropdown';
import { bs } from '../../styles/styles';
import { styleSheet } from './styles';

type FontSourceType = 'google' | 'web';

interface FontSourceOption {
	source: FontSourceType;
	id: string;
	label: string;
}

interface FontOption {
	name: string;
	uri?: string;
	id: string;
}

export interface FontPickerProps {
	/** Current font value */
	value?: FontSelection;
	/** Called when font selection changes */
	onChange: (font: FontSelection) => void;
}

const FONT_SOURCES: FontSourceOption[] = [
	{ source: 'google', id: 'google-font', label: 'Google Fonts' },
	{ source: 'web', id: 'web-font', label: 'Web Safe Font' },
];

const WEB_SAFE_FONTS: FontOption[] = [
	{ name: 'Arial', id: 'arial' },
	{ name: 'Verdana', id: 'verdana' },
	{ name: 'Tahoma', id: 'tahoma' },
	{ name: 'Trebuchet MS', id: 'trebuchet-ms' },
	{ name: 'Times New Roman', id: 'times-new-roman' },
	{ name: 'Georgia', id: 'georgia' },
	{ name: 'Garamond', id: 'garamond' },
	{ name: 'Courier New', id: 'courier-new' },
];

const GOOGLE_FONTS: FontOption[] = [
	{ name: 'Lato', uri: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap', id: 'lato' },
	{
		name: 'Merriweather',
		uri: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap',
		id: 'merriweather',
	},
	{
		name: 'Montserrat',
		uri: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap',
		id: 'montserrat',
	},
	{ name: 'Nunito', uri: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;700&display=swap', id: 'nunito' },
	{
		name: 'Open Sans',
		uri: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700&display=swap',
		id: 'open-sans',
	},
	{ name: 'Oswald', uri: 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;700&display=swap', id: 'oswald' },
	{
		name: 'Playfair Display',
		uri: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&display=swap',
		id: 'playfair-display',
	},
	{ name: 'Poppins', uri: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&display=swap', id: 'poppins' },
	{ name: 'PT Sans', uri: 'https://fonts.googleapis.com/css2?family=PT+Sans:wght@400;700&display=swap', id: 'pt-sans' },
	{
		name: 'Quicksand',
		uri: 'https://fonts.googleapis.com/css2?family=Quicksand:wght@400;700&display=swap',
		id: 'quicksand',
	},
	{ name: 'Raleway', uri: 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;700&display=swap', id: 'raleway' },
	{ name: 'Roboto', uri: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap', id: 'roboto' },
	{
		name: 'Source Sans Pro',
		uri: 'https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;700&display=swap',
		id: 'source-sans-pro',
	},
	{ name: 'Ubuntu', uri: 'https://fonts.googleapis.com/css2?family=Ubuntu:wght@400;700&display=swap', id: 'ubuntu' },
	{
		name: 'Work Sans',
		uri: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;700&display=swap',
		id: 'work-sans',
	},
];

const CUSTOM_URL_OPTION: FontOption = {
	name: 'Custom URL...',
	id: 'custom-url',
};

export const FontPicker: FC<FontPickerProps> = ({ value, onChange }) => {
	const [isEditing, setIsEditing] = useState(!value?.fontName);
	const [fontSource, setFontSource] = useState<FontSourceOption | null>(null);
	const [showCustomUrlInput, setShowCustomUrlInput] = useState(false);
	const [customUrl, setCustomUrl] = useState('');

	// Determine current font info for preview
	const fontInfo = useMemo(() => {
		if (!value?.fontName) return null;

		const isGoogle = Boolean(value.fontUri);
		const knownGoogleFont = GOOGLE_FONTS.find(f => f.uri === value.fontUri);

		return {
			name: value.fontName,
			source: isGoogle ? 'Google Fonts' : 'Web Safe Font',
			isCustomUrl: isGoogle && !knownGoogleFont,
			uri: value.fontUri,
		};
	}, [value]);

	// Initialize state from value
	useEffect(() => {
		if (value?.fontUri) {
			setFontSource(FONT_SOURCES[0]); // Google
			setIsEditing(false);
			const knownFont = GOOGLE_FONTS.find(f => f.uri === value.fontUri);
			if (!knownFont) {
				setShowCustomUrlInput(true);
				setCustomUrl(value.fontUri);
			}
		} else if (value?.fontName) {
			setFontSource(FONT_SOURCES[1]); // Web safe
			setIsEditing(false);
		}
	}, [value]);

	const handleSourceSelect = useCallback((source: FontSourceOption) => {
		setFontSource(source);
		setShowCustomUrlInput(false);
	}, []);

	const handleWebFontSelect = useCallback(
		(font: FontOption) => {
			onChange({ fontName: font.name, fontUri: '' });
			setIsEditing(false);
		},
		[onChange]
	);

	const handleGoogleFontSelect = useCallback(
		(font: FontOption) => {
			if (font.id === 'custom-url') {
				setShowCustomUrlInput(true);
				setCustomUrl('');
			} else {
				onChange({ fontName: font.name, fontUri: font.uri || '' });
				setIsEditing(false);
				setShowCustomUrlInput(false);
			}
		},
		[onChange]
	);

	const handleCustomUrlChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const input = e.target.value;
			setCustomUrl(input);
			const { fontUri, fontName } = parseGoogleFontInput(input);
			if (fontName && fontUri) {
				onChange({ fontName, fontUri });
			}
		},
		[onChange]
	);

	const handleCustomUrlDone = useCallback(() => {
		if (value?.fontName && value?.fontUri) {
			setIsEditing(false);
		}
	}, [value]);

	const handleChange = useCallback(() => {
		setIsEditing(true);
	}, []);

	const handleClear = useCallback(() => {
		onChange({ fontName: '', fontUri: '' });
		setFontSource(null);
		setShowCustomUrlInput(false);
		setCustomUrl('');
		setIsEditing(true);
	}, [onChange]);

	// Preview mode - show selected font
	if (!isEditing && fontInfo) {
		return (
			<div className={css(styleSheet.preview)}>
				{fontInfo.uri && <link href={fontInfo.uri} rel='stylesheet' />}
				<div className={css(styleSheet.previewContent)}>
					<div className={css(styleSheet.previewName)} style={{ fontFamily: fontInfo.name }}>
						{fontInfo.name}
					</div>
					<div className={css(styleSheet.previewSource)}>
						{fontInfo.source}
						{fontInfo.isCustomUrl && ' (Custom)'}
					</div>
					<div className={css(styleSheet.previewSample)} style={{ fontFamily: fontInfo.name }}>
						The quick brown fox jumps over the lazy dog
					</div>
				</div>
				<div className={css(styleSheet.previewActions)}>
					<Button variant='secondary' size='sm' onClick={handleChange}>
						Change
					</Button>
					<Button variant='link' size='sm' onClick={handleClear}>
						Clear
					</Button>
				</div>
			</div>
		);
	}

	// Edit mode
	return (
		<div className={css(styleSheet.editor)}>
			{/* Font Source Selection */}
			<div className={css(styleSheet.field)}>
				<label className={css(styleSheet.label)}>Font Source</label>
				<Dropdown>
					<DropdownSelectTrigger styles={[styleSheet.trigger]}>
						{fontSource?.label || 'Select source'}
					</DropdownSelectTrigger>
					<DropdownContent align='start' styles={[styleSheet.dropdownContent]}>
						{FONT_SOURCES.map(source => (
							<DropdownItem
								key={source.id}
								onClick={() => handleSourceSelect(source)}
								styles={[fontSource?.id === source.id && bs.bgGray200]}
							>
								{source.label}
							</DropdownItem>
						))}
					</DropdownContent>
				</Dropdown>
			</div>

			{/* Google Fonts Selection */}
			{fontSource?.source === 'google' && (
				<div className={css(styleSheet.field)}>
					<label className={css(styleSheet.label)}>Select Font</label>
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.trigger]}>
							{value?.fontName || 'Select font'}
						</DropdownSelectTrigger>
						<DropdownContent align='start' styles={[styleSheet.dropdownContent, styleSheet.fontList]}>
							{GOOGLE_FONTS.map(font => (
								<DropdownItem
									key={font.id}
									onClick={() => handleGoogleFontSelect(font)}
									styles={[value?.fontUri === font.uri && bs.bgGray200]}
								>
									{font.name}
								</DropdownItem>
							))}
							<DropdownItem
								key={CUSTOM_URL_OPTION.id}
								onClick={() => handleGoogleFontSelect(CUSTOM_URL_OPTION)}
								styles={[styleSheet.customUrlOption]}
							>
								{CUSTOM_URL_OPTION.name}
							</DropdownItem>
						</DropdownContent>
					</Dropdown>

					{showCustomUrlInput && (
						<div className={css(styleSheet.customUrlField)}>
							<label className={css(styleSheet.label)}>Google Font URL</label>
							<input
								type='text'
								value={customUrl}
								onChange={handleCustomUrlChange}
								placeholder='Paste Google Fonts URL or embed code'
								className={css(styleSheet.input)}
							/>
							{value?.fontName && value?.fontUri && (
								<Button variant='primary' size='sm' onClick={handleCustomUrlDone} styles={[styleSheet.doneButton]}>
									Done
								</Button>
							)}
						</div>
					)}
				</div>
			)}

			{/* Web Safe Fonts Selection */}
			{fontSource?.source === 'web' && (
				<div className={css(styleSheet.field)}>
					<label className={css(styleSheet.label)}>Select Font</label>
					<Dropdown>
						<DropdownSelectTrigger styles={[styleSheet.trigger]}>
							{value?.fontName || 'Select font'}
						</DropdownSelectTrigger>
						<DropdownContent align='start' styles={[styleSheet.dropdownContent]}>
							{WEB_SAFE_FONTS.map(font => (
								<DropdownItem
									key={font.id}
									onClick={() => handleWebFontSelect(font)}
									styles={[value?.fontName === font.name && bs.bgGray200]}
								>
									{font.name}
								</DropdownItem>
							))}
						</DropdownContent>
					</Dropdown>
				</div>
			)}

			{/* Cancel button when editing existing font */}
			{fontInfo && (
				<div className={css(styleSheet.editActions)}>
					<Button variant='link' size='sm' onClick={() => setIsEditing(false)}>
						Cancel
					</Button>
				</div>
			)}
		</div>
	);
};
